import { Building2, Star } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { SurveyResponse, TemplateQuestion } from '@/api/orderDetails'
import { useLocalization } from '@/contexts/LocalizationContext'
import { formatOrderDateTime } from '@/hooks/useOrders'
import {
  getResponseCompletionPercentage,
  getSurveyResponseTitle,
  mapSubmittedResponseAnswersToClientAnswers,
} from '@/utils/orderSurveyResponses'
import { SurveyQuestionDisplay } from './SurveyQuestionDisplay'

interface SurveyResponseCardProps {
  response: SurveyResponse
  locale: 'en' | 'ar'
  templateQuestionsForLookup?: TemplateQuestion[]
  accent?: 'green' | 'amber' | 'blue' | 'purple'
}

const ACCENT_STYLES = {
  green: 'border-green-200 bg-green-50/80',
  amber: 'border-amber-200 bg-amber-50/80',
  blue: 'border-blue-200 bg-blue-50/80',
  purple: 'border-purple-200 bg-purple-50/80',
}

/** Decimal fields can arrive as strings over GraphQL; treat 0 and unparseable values as "no rating". */
const parseRating = (value: number | string | null | undefined): number | null => {
  if (value == null) return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function SurveyResponseCard({
  response,
  locale,
  templateQuestionsForLookup,
  accent = 'green',
}: SurveyResponseCardProps) {
  const { t } = useLocalization()
  const surveyTitle = getSurveyResponseTitle(response, locale)
  const respondent = response.respondent
  const providerProfile = respondent?.providerProfile ?? null
  // Prefer the provider profile's own name — it is the display name the provider registered with.
  const respondentName =
    providerProfile?.fullName?.trim() ||
    [respondent?.firstName, respondent?.lastName].filter(Boolean).join(' ').trim()
  const rating = parseRating(providerProfile?.averageRating)
  const submittedAnswers = mapSubmittedResponseAnswersToClientAnswers(response, locale)
  const completionPercentage = getResponseCompletionPercentage(response)

  return (
    <div className={`rounded-lg border p-4 ${ACCENT_STYLES[accent]}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-medium text-gray-900">{surveyTitle}</h4>
          {respondent && (
            <div className="mt-1 space-y-1">
              <p className="text-sm text-gray-600">
                {t('orderDetails.respondent')}: {respondentName}
              </p>
              {providerProfile?.organizationName && (
                <p className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{providerProfile.organizationName}</span>
                </p>
              )}
              {providerProfile && (rating !== null || providerProfile.ratingDisplay) && (
                <p className="flex items-center gap-1 text-xs text-gray-600">
                  <Star className="h-3.5 w-3.5 shrink-0 fill-yellow-500 text-yellow-500" />
                  {rating !== null ? (
                    <>
                      <span className="font-medium">{rating.toFixed(1)}</span>
                      {providerProfile.totalReviews != null && (
                        <span className="text-gray-500">
                          ({providerProfile.totalReviews} {t('providers.reviews')})
                        </span>
                      )}
                    </>
                  ) : (
                    /* No numeric rating — fall back to the backend's preformatted string. */
                    <span className="font-medium">{providerProfile.ratingDisplay}</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>
        <Badge
          className={
            response.submitted
              ? 'bg-brand-primary text-white border-0 shrink-0'
              : 'bg-gray-100 text-gray-700 border-0 shrink-0'
          }
        >
          {response.submitted ? t('orderDetails.surveyCompleted') : t('orderDetails.surveyPending')}
        </Badge>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-600">
        <span>
          {t('orderDetails.completionPercentage')}: {completionPercentage}%
        </span>
        {response.submittedAt && (
          <span>{formatOrderDateTime(response.submittedAt, locale)}</span>
        )}
      </div>

      <div className="mt-2 w-full bg-white/80 rounded-full h-1.5">
        <div
          className="bg-brand-primary h-1.5 rounded-full transition-all"
          style={{ width: `${completionPercentage}%` }}
        />
      </div>

      {submittedAnswers.length > 0 && (
        <div className="mt-4 space-y-3 border-t border-gray-200/70 pt-3">
          {submittedAnswers.map((answer, index) => (
            <SurveyQuestionDisplay
              key={`${response.id}-${answer.templateQuestionId}-${index}`}
              answer={answer}
              locale={locale}
              templateQuestionsForLookup={templateQuestionsForLookup}
            />
          ))}
        </div>
      )}
    </div>
  )
}
